import {
  WorkspaceActionTypes,
  WorkspaceAction,
  WorkspaceState,
} from './workspace.types';

// Initial state
const initialState: WorkspaceState = {
  // Workspace data
  workspaces: [],
  adminWorkspaces: [],
  archivedWorkspaces: [],
  workspace: null,
  // Loading states
  loading: false,
  getAllWorkspacesLoading: false,
  getAdminWorkspacesLoading: false,
  getArchivedWorkspacesLoading: false,
  archiveWorkspaceLoading: false,
  getWorkspaceByIdLoading: false,
  createWorkspaceLoading: false,
  updateWorkspaceLoading: false,
  deleteWorkspaceLoading: false,
  switchWorkspaceLoading: false,
  removeUserLoading: false,
  updateWorkspaceSettingsLoading: false,

  // Error states
  error: null,
  getAllWorkspacesError: null,
  getAdminWorkspacesError: null,
  getArchivedWorkspacesError: null,
  archiveWorkspaceError: null,
  getWorkspaceByIdError: null,
  createWorkspaceError: null,
  updateWorkspaceError: null,
  deleteWorkspaceError: null,
  switchWorkspaceError: null,
  removeUserError: null,
  updateWorkspaceSettingsError: null,

  // Success states
  success: null,
  getAllWorkspacesSuccess: false,
  getAdminWorkspacesSuccess: false,
  getArchivedWorkspacesSuccess: false,
  archiveWorkspaceSuccess: false,
  getWorkspaceByIdSuccess: false,
  createWorkspaceSuccess: false,
  updateWorkspaceSuccess: false,
  deleteWorkspaceSuccess: false,
  switchWorkspaceSuccess: false,
  removeUserSuccess: false,
  updateWorkspaceSettingsSuccess: null,
};

// Workspace reducer
const workspaceReducer = (
  state: WorkspaceState = initialState,
  action: WorkspaceAction
): WorkspaceState => {
  switch (action.type) {
    // Get all workspaces actions
    case WorkspaceActionTypes.GET_ALL_WORKSPACES_REQUEST:
      return {
        ...state,
        getAllWorkspacesLoading: true,
        getAllWorkspacesError: null,
        getAllWorkspacesSuccess: false,
      };

    case WorkspaceActionTypes.GET_ALL_WORKSPACES_SUCCESS:
      return {
        ...state,
        getAllWorkspacesLoading: false,
        getAllWorkspacesError: null,
        getAllWorkspacesSuccess: true,
        workspaces: action.payload,
      };

    case WorkspaceActionTypes.GET_ALL_WORKSPACES_FAILURE:
      return {
        ...state,
        getAllWorkspacesLoading: false,
        getAllWorkspacesError: action.payload,
        getAllWorkspacesSuccess: false,
      };

    // Get admin workspaces actions
    case WorkspaceActionTypes.GET_ADMIN_WORKSPACES_REQUEST:
      return {
        ...state,
        getAdminWorkspacesLoading: true,
        getAdminWorkspacesError: null,
        getAdminWorkspacesSuccess: false,
      };

    case WorkspaceActionTypes.GET_ADMIN_WORKSPACES_SUCCESS:
      return {
        ...state,
        getAdminWorkspacesLoading: false,
        getAdminWorkspacesError: null,
        getAdminWorkspacesSuccess: true,
        adminWorkspaces: action.payload,
      };

    case WorkspaceActionTypes.GET_ADMIN_WORKSPACES_FAILURE:
      return {
        ...state,
        getAdminWorkspacesLoading: false,
        getAdminWorkspacesError: action.payload,
        getAdminWorkspacesSuccess: false,
      };

    // Get archived workspaces actions
    case WorkspaceActionTypes.GET_ARCHIVED_WORKSPACES_REQUEST:
      return {
        ...state,
        getArchivedWorkspacesLoading: true,
        getArchivedWorkspacesError: null,
        getArchivedWorkspacesSuccess: false,
      };

    case WorkspaceActionTypes.GET_ARCHIVED_WORKSPACES_SUCCESS:
      return {
        ...state,
        getArchivedWorkspacesLoading: false,
        getArchivedWorkspacesError: null,
        getArchivedWorkspacesSuccess: true,
        archivedWorkspaces: action.payload,
      };

    case WorkspaceActionTypes.GET_ARCHIVED_WORKSPACES_FAILURE:
      return {
        ...state,
        getArchivedWorkspacesLoading: false,
        getArchivedWorkspacesError: action.payload,
        getArchivedWorkspacesSuccess: false,
      };

    // Archive workspace actions
    case WorkspaceActionTypes.ARCHIVE_WORKSPACE_REQUEST:
      return {
        ...state,
        archiveWorkspaceLoading: true,
        archiveWorkspaceError: null,
        archiveWorkspaceSuccess: false,
      };

    case WorkspaceActionTypes.ARCHIVE_WORKSPACE_SUCCESS:
      return {
        ...state,
        archiveWorkspaceLoading: false,
        archiveWorkspaceError: null,
        archiveWorkspaceSuccess: true,
        adminWorkspaces: state.adminWorkspaces.filter(
          (w) => w._id !== action.payload
        ),
      };

    case WorkspaceActionTypes.ARCHIVE_WORKSPACE_FAILURE:
      return {
        ...state,
        archiveWorkspaceLoading: false,
        archiveWorkspaceError: action.payload,
        archiveWorkspaceSuccess: false,
      };

    // Get workspace by ID actions
    case WorkspaceActionTypes.GET_WORKSPACE_BY_ID_REQUEST:
      return {
        ...state,
        getWorkspaceByIdLoading: true,
        getWorkspaceByIdError: null,
        getWorkspaceByIdSuccess: false,
      };

    case WorkspaceActionTypes.GET_WORKSPACE_BY_ID_SUCCESS:
      return {
        ...state,
        getWorkspaceByIdLoading: false,
        getWorkspaceByIdError: null,
        getWorkspaceByIdSuccess: true,
        workspace: action.payload,
      };

    case WorkspaceActionTypes.GET_WORKSPACE_BY_ID_FAILURE:
      return {
        ...state,
        getWorkspaceByIdLoading: false,
        getWorkspaceByIdError: action.payload,
        getWorkspaceByIdSuccess: false,
        workspace: null,
      };

    // Create workspace actions
    case WorkspaceActionTypes.CREATE_WORKSPACE_REQUEST:
      return {
        ...state,
        createWorkspaceLoading: true,
        createWorkspaceError: null,
        createWorkspaceSuccess: false,
      };

    case WorkspaceActionTypes.CREATE_WORKSPACE_SUCCESS:
      return {
        ...state,
        createWorkspaceLoading: false,
        createWorkspaceError: null,
        createWorkspaceSuccess: true,
        workspaces: [...state.workspaces, action.payload],
      };

    case WorkspaceActionTypes.CREATE_WORKSPACE_FAILURE:
      return {
        ...state,
        createWorkspaceLoading: false,
        createWorkspaceError: action.payload,
        createWorkspaceSuccess: false,
      };

    // Update workspace actions
    case WorkspaceActionTypes.UPDATE_WORKSPACE_REQUEST:
      return {
        ...state,
        updateWorkspaceLoading: true,
        updateWorkspaceError: null,
        updateWorkspaceSuccess: false,
      };

    case WorkspaceActionTypes.UPDATE_WORKSPACE_SUCCESS: {
      const updatedWorkspaceId =
        action.payload._id || state.workspace?._id || null;

      return {
        ...state,
        updateWorkspaceLoading: false,
        updateWorkspaceError: null,
        updateWorkspaceSuccess: true,
        workspace: state.workspace
          ? { ...state.workspace, ...action.payload }
          : state.workspace,
        workspaces: updatedWorkspaceId
          ? state.workspaces.map((workspace) =>
              workspace._id === updatedWorkspaceId
                ? { ...workspace, ...action.payload }
                : workspace
            )
          : state.workspaces,
        adminWorkspaces: updatedWorkspaceId
          ? state.adminWorkspaces.map((workspace) =>
              workspace._id === updatedWorkspaceId
                ? { ...workspace, ...action.payload }
                : workspace
            )
          : state.adminWorkspaces,
      };
    }

    case WorkspaceActionTypes.UPDATE_WORKSPACE_FAILURE:
      return {
        ...state,
        updateWorkspaceLoading: false,
        updateWorkspaceError: action.payload,
        updateWorkspaceSuccess: false,
      };

    // Delete workspace actions
    case WorkspaceActionTypes.DELETE_WORKSPACE_REQUEST:
      return {
        ...state,
        deleteWorkspaceLoading: true,
        deleteWorkspaceError: null,
        deleteWorkspaceSuccess: false,
      };

    case WorkspaceActionTypes.DELETE_WORKSPACE_SUCCESS:
      return {
        ...state,
        deleteWorkspaceLoading: false,
        deleteWorkspaceError: null,
        deleteWorkspaceSuccess: true,
        workspaces: state.workspaces.filter(
          (workspace) => workspace._id !== action.payload
        ),
      };

    case WorkspaceActionTypes.DELETE_WORKSPACE_FAILURE:
      return {
        ...state,
        deleteWorkspaceLoading: false,
        deleteWorkspaceError: action.payload,
        deleteWorkspaceSuccess: false,
      };

    // Switch workspace actions
    case WorkspaceActionTypes.SWITCH_WORKSPACE_REQUEST:
      return {
        ...state,
        switchWorkspaceLoading: true,
        switchWorkspaceError: null,
        switchWorkspaceSuccess: false,
      };

    case WorkspaceActionTypes.SWITCH_WORKSPACE_SUCCESS:
      return {
        ...state,
        switchWorkspaceLoading: false,
        switchWorkspaceError: null,
        switchWorkspaceSuccess: true,
      };

    case WorkspaceActionTypes.SWITCH_WORKSPACE_FAILURE:
      return {
        ...state,
        switchWorkspaceLoading: false,
        switchWorkspaceError: action.payload,
        switchWorkspaceSuccess: false,
      };

    // Remove user actions
    case WorkspaceActionTypes.REMOVE_USER_FROM_WORKSPACE_REQUEST:
      return {
        ...state,
        removeUserLoading: true,
        removeUserError: null,
        removeUserSuccess: false,
      };

    case WorkspaceActionTypes.REMOVE_USER_FROM_WORKSPACE_SUCCESS:
      return {
        ...state,
        removeUserLoading: false,
        removeUserError: null,
        removeUserSuccess: true,
        success: action.payload,
      };

    case WorkspaceActionTypes.REMOVE_USER_FROM_WORKSPACE_FAILURE:
      return {
        ...state,
        removeUserLoading: false,
        removeUserError: action.payload,
        removeUserSuccess: false,
      };

    // Update workspace settings actions
    case WorkspaceActionTypes.UPDATE_WORKSPACE_SETTINGS_REQUEST:
      return {
        ...state,
        updateWorkspaceSettingsLoading: true,
        updateWorkspaceSettingsError: null,
        updateWorkspaceSettingsSuccess: null,
      };

    case WorkspaceActionTypes.UPDATE_WORKSPACE_SETTINGS_SUCCESS:
      return {
        ...state,
        updateWorkspaceSettingsLoading: false,
        workspace: state.workspace
          ? {
              ...state.workspace,
              duplicateEmailEnabled: action.payload.duplicateEmailEnabled,
            }
          : null,
        updateWorkspaceSettingsSuccess: action.payload.message,
        updateWorkspaceSettingsError: null,
      };

    case WorkspaceActionTypes.UPDATE_WORKSPACE_SETTINGS_FAILURE:
      return {
        ...state,
        updateWorkspaceSettingsLoading: false,
        updateWorkspaceSettingsError: action.payload,
        updateWorkspaceSettingsSuccess: null,
      };

    // Clear workspace state actions
    case WorkspaceActionTypes.CLEAR_WORKSPACE_STATE:
      return {
        ...state,
        error: null,
        getAllWorkspacesError: null,
        getAdminWorkspacesError: null,
        getArchivedWorkspacesError: null,
        archiveWorkspaceError: null,
        getWorkspaceByIdError: null,
        createWorkspaceError: null,
        updateWorkspaceError: null,
        deleteWorkspaceError: null,
        switchWorkspaceError: null,
        removeUserError: null,
        updateWorkspaceSettingsError: null,
        success: null,
        getAllWorkspacesSuccess: false,
        getAdminWorkspacesSuccess: false,
        getArchivedWorkspacesSuccess: false,
        archiveWorkspaceSuccess: false,
        getWorkspaceByIdSuccess: false,
        createWorkspaceSuccess: false,
        updateWorkspaceSuccess: false,
        deleteWorkspaceSuccess: false,
        switchWorkspaceSuccess: false,
        removeUserSuccess: false,
        updateWorkspaceSettingsSuccess: null,
      };

    // Clear workspace data
    case WorkspaceActionTypes.CLEAR_WORKSPACE_DATA:
      return {
        ...initialState,
      };

    default:
      return state;
  }
};

export default workspaceReducer;
